import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { map, catchError, of } from 'rxjs';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { SelectPesquisavelService } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.service';
import {
  PesquisaCodigoTributacaoModalComponent,
  PesquisaCodigoTributacaoModalData,
  PesquisaCodigoTributacaoModalResult,
} from '../../../../shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { TipoNcmEnum } from '../../../../shared/enums/motor-fiscal/tipo-ncm.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { ConfiguracaoProdutoService } from '../../../../shared/services/apis/produto/configuracao-produto.service';
import { LookupService } from '../../../../shared/services/apis/produto/produto-lookup.service';
import { MotorFiscalLookupService } from '../../../../shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { UnidadeMedidaService } from '../../../../shared/services/apis/produto/unidade-medida.service';
import { ConfiguracaoProdutoRequest } from '../../../../shared/services/requests/produto/configuracao-produto.request';
import { LookupItemStruct } from '../../../../shared/services/structs/pessoa/lookup-item.struct';
import { Masks, MaskService } from '../../../../shared/services/mask.service';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-config-produto',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    MatDialogModule,
    HeaderComponent,
    SelectPesquisavelComponent,
    NgxMaskDirective,
    MatProgressSpinnerModule,
    FooterActionComponent,
    LoadingBoxDefaultListComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './config-produto.component.html',
  styleUrls: ['./config-produto.component.css'],
})
export class ConfigProdutoComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public form: FormGroup;
  public isLoading: boolean = true;
  public isSaving: boolean = false;

  public listaTipoSoma: LookupItemStruct[] = [];
  public listaTipoGeracaoSku: LookupItemStruct[] = [];
  public listaTipoCustoKitFabricado: LookupItemStruct[] = [];
  public listaOrigemMercadoria: LookupItemStruct[] = [];
  public listaUnidadeMedida: LookupItemStruct[] = [];

  public masks: Masks = MaskService.getMasks();

  public urlNcm = `${environment.urlApiMotorFiscal}Lookup/ncm?tipoNcm=${TipoNcmEnum.SubItem}`;

  private idConfiguracaoProduto: number = 0;

  constructor(
    private formBuilder: FormBuilder,
    private configuracaoProdutoService: ConfiguracaoProdutoService,
    private lookupService: LookupService,
    private motorFiscalLookupService: MotorFiscalLookupService,
    private unidadeMedidaService: UnidadeMedidaService,
    private alertService: AlertService,
    private router: Router,
    private dialog: MatDialog,
    private selectPesquisavelService: SelectPesquisavelService
  ) {}

  get ncmControl(): FormControl {
    return this.form.get('ncmPadrao') as FormControl;
  }

  ngOnInit(): void {
    this.createForm();
    this.carregarLookups();
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      idTipoSomaPeso: [null],
      idTipoGeracaoSku: [null],
      idTipoCustoKitFabricado: [null],
      isCadastroAutomatico: [false],
      idUnidadeMedidaPadrao: [null],
      ncmPadrao: [''],
      idOrigemMercadoriaPadrao: [null],
    });
  }

  private carregarLookups(): void {
    this.isLoading = true;

    // Origem da Mercadoria é obtida de db_motor_fiscal via API MotorFiscal.
    this.motorFiscalLookupService.getLookup().subscribe({
      next: res => {
        if (!res.isError) {
          this.listaOrigemMercadoria = res.listaOrigemMercadoria || [];
        }
      },
    });

    // Load general lookups
    this.lookupService.getLookup().subscribe({
      next: res => {
        if (res.isError) {
          this.showAlertError(res.errorDescription || 'Erro ao carregar listas.');
          return;
        }
        this.listaTipoSoma = res.listaTipoSoma || [];
        this.listaTipoGeracaoSku = res.listaTipoGeracaoSku || [];
        this.listaTipoCustoKitFabricado = res.listaTipoCustoKitFabricado || [];

        // Load Unit of Measure lookup
        this.carregarUnidadesMedida();
      },
      error: err => {
        this.showAlertError('Ocorreu um erro ao carregar as listas.');
        console.error(err);
        this.isLoading = false;
      },
    });
  }

  private carregarUnidadesMedida(): void {
    this.unidadeMedidaService.getLookup().subscribe({
      next: res => {
        if (res.isError) {
          this.showAlertError(res.errorDescription || 'Erro ao carregar unidades de medida.');
          return;
        }
        this.listaUnidadeMedida = res.list || [];
        this.carregarConfiguracao();
      },
      error: err => {
        this.showAlertError('Ocorreu um erro ao carregar unidades de medida.');
        console.error(err);
        this.isLoading = false;
      },
    });
  }

  private carregarConfiguracao(): void {
    this.configuracaoProdutoService.get().subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }

        if (res.configuracaoProduto) {
          this.idConfiguracaoProduto = res.configuracaoProduto.idConfiguracaoProduto;
          this.form.patchValue({
            idTipoSomaPeso: res.configuracaoProduto.idTipoSomaPeso,
            idTipoGeracaoSku: res.configuracaoProduto.idTipoGeracaoSku,
            idTipoCustoKitFabricado: res.configuracaoProduto.idTipoCustoKitFabricado,
            isCadastroAutomatico: res.configuracaoProduto.isCadastroAutomatico,
            idUnidadeMedidaPadrao: res.configuracaoProduto.idUnidadeMedidaPadrao,
            ncmPadrao: res.configuracaoProduto.ncmPadrao,
            idOrigemMercadoriaPadrao: res.configuracaoProduto.idOrigemMercadoriaPadrao,
          });
        }
      },
      error: err => {
        this.isLoading = false;
        this.showAlertError('Ocorreu um erro ao carregar as configurações.');
        console.error(err);
      },
    });
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Verifique os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request: ConfiguracaoProdutoRequest = this.form.value;

    this.configuracaoProdutoService.put(this.idConfiguracaoProduto, request).subscribe({
      next: res => {
        this.isSaving = false;
        if (res.isError) {
          this.showAlertError(res.errorDescription || 'Erro ao salvar.');
        } else {
          this.alertService.show('Sucesso', 'Configurações salvas com sucesso.', AlertType.success);
        }
      },
      error: err => {
        this.isSaving = false;
        this.showAlertError('Ocorreu um erro ao salvar.');
        console.error(err);
      },
    });
  }

  public getControl(controlName: string): any {
    return this.form.get(controlName);
  }

  public pesquisarNcm(): void {
    const url = this.urlNcm;
    const dialogRef = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
      width: '700px',
      maxHeight: '85vh',
      panelClass: 'pesquisa-codigo-tributacao-dialog',
      data: {
        titulo: 'Pesquisar NCM',
        buscarFn: (pesquisa: string) =>
          this.selectPesquisavelService.getSelectValues<any>(url, pesquisa).pipe(
            map(list => ({
              isError: false,
              itens: list.map((i: any) => ({ codigo: String(i.codigo ?? ''), descricao: i.descricao })),
            })),
            catchError(() => of({ isError: true, itens: [] }))
          ),
        permitirManual: false,
        registrosIniciais: 20,
        valorInicial: this.ncmControl?.value ?? undefined,
      } as PesquisaCodigoTributacaoModalData,
    });
    dialogRef.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
      if (!result?.selecionado) return;
      this.ncmControl?.setValue(result.selecionado.codigo.replace(/\D/g, ''));
      this.ncmControl?.markAsDirty();
    });
  }

  public cancelar(): void {
    this.router.navigate(['/configuracoes/cadastros']);
  }

  private showAlertError(msg: string): void {
    this.alertService.show('Erro', msg, AlertType.error);
  }
}
